import { Badge } from "@/components/ui/badge";
import type { StrategySnapshot } from "@/lib/strategy-snapshot";
import { getProductForecast } from "@/mocks/strategy-product";
import { SALES_GOAL } from "./use-strategy-wizard";

export function ProductForecast({
  snapshot,
  goal,
  targetRank,
}: {
  snapshot: StrategySnapshot;
  goal: string;
  targetRank: string;
}) {
  const forecast = getProductForecast(snapshot, goal);
  const isDemoRank = goal === SALES_GOAL && targetRank === "11–20 名";
  const formatUsd = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Math.round(amount / 100) * 100);
  const formatCompactUsd = (amount: number) => `$${(amount / 1000).toFixed(1)}k`;
  // Keep the requested presentation figures scoped to the 11–20 rank demo.
  const salesValue = isDemoRank
    ? "约 $280k"
    : forecast.modeledReallocation
      ? `${formatCompactUsd(forecast.salesRange[0])}–${formatCompactUsd(forecast.salesRange[1])}`
      : `约 ${formatUsd(forecast.salesRange[0])}`;
  const costShareValue = isDemoRank
    ? "8–12%"
    : forecast.adCostShareRange
      ? forecast.modeledReallocation
        ? `${(forecast.adCostShareRange[0] * 100).toFixed(1)}–${(forecast.adCostShareRange[1] * 100).toFixed(1)}%`
        : `${(forecast.adCostShareRange[1] * 100).toFixed(1)}%`
      : "暂无数据";
  const cards = [
    {
      title: forecast.modeledReallocation ? "广告归因销售额情景" : "广告归因销售额基线",
      value: salesValue,
      detail: isDemoRank
        ? "目标小类排名 11–20 名"
        : forecast.modeledReallocation
          ? "功能词承接效果从无改善到历史效率"
          : "仅按现有投放节奏推算",
    },
    {
      title: "预估广告花费",
      value: `约 ${formatUsd(isDemoRank ? 26_000 : forecast.adSpend)}`,
      detail: isDemoRank
        ? "未来 14 天广告花费预估"
        : forecast.modeledReallocation
          ? `总花费不增，内部调配约 ${formatUsd(forecast.reallocatedSpend)}`
          : "目标执行条件未核实，维持现有总花费",
    },
    {
      title: "预估小类排名",
      value: isDemoRank ? "12 名" : `约 ${forecast.rankRange[0]}–${forecast.rankRange[1]} 名`,
      detail: `${forecast.rankBasis.category} 当前 #${forecast.rankBasis.currentRank}${goal === SALES_GOAL && targetRank ? ` · 目标 ${targetRank}` : ""}`,
    },
    {
      title: forecast.modeledReallocation ? "广告费占比（TACOS）情景" : "广告费占比（TACOS）基线",
      value: costShareValue,
      detail: "广告花费 ÷ 总支出",
    },
  ];

  return (
    <section aria-labelledby="strategy-forecast-heading" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 id="strategy-forecast-heading" className="text-lg font-semibold">
          {forecast.requiresValidation ? "未来 14 天经营基线与目标验证" : "未来 14 天策略情景预测"}
        </h2>
        <Badge variant="neutral">
          {goal} · {forecast.requiresValidation ? "五项候选打法" : "五项打法"}
        </Badge>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <div
            key={card.title}
            className="flex flex-col gap-3 rounded-[10px] border border-line p-5"
          >
            <h3 className="text-[13px] text-muted-foreground">{card.title}</h3>
            <p className="font-mono text-[24px] font-medium leading-9 tracking-[-0.04em]">
              {card.value}
            </p>
            <p className="text-xs leading-5 text-muted-foreground">{card.detail}</p>
          </div>
        ))}
      </div>
      <p className="text-xs leading-5 text-muted-foreground">
        {isDemoRank ? (
          <>
            根据“{goal}”目标和“{targetRank}”目标小类排名展示预测结果。
          </>
        ) : forecast.modeledReallocation ? (
          <>
            根据“{goal}”目标和功能词精准承接打法，以历史 {forecast.basis.periodDays}{" "}
            天广告数据为基线； 假设类别主题组约 {(forecast.reallocationShare * 100).toFixed(0)}%
            的花费转向已成交功能词，总花费不变。转出花费以商品整体 ACoS（
            {((forecast.baselineAcos ?? 0) * 100).toFixed(1)}
            %）作为近似，转入花费以功能词中较高的历史 ACoS（
            {((forecast.targetAcos ?? 0) * 100).toFixed(1)}
            %）推演。区间包含“没有改善”情景，不保证转入后的效率。
          </>
        ) : (
          <>
            根据“{goal}”目标和当前策略，{forecast.limitation} 上述金额与占比仅按历史{" "}
            {forecast.basis.periodDays} 天日均表现推算，不能视为目标达成预测。
          </>
        )}{" "}
        销售额仅指广告归因销售额。
        {!isDemoRank && (
          <>
            小类排名按当前 #{forecast.rankBasis.currentRank} 与此前 #
            {forecast.rankBasis.previousRank} 两次观察粗估：以名次持平和近期变化延续为区间边界；
            尚未量化所选目标与打法对排名的影响，实际名次可能超出区间。
          </>
        )}
        <a
          href={forecast.rankBasis.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-1 underline underline-offset-2"
        >
          查看 Amazon 商品页
        </a>
        。
      </p>
    </section>
  );
}
